import { Box } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
    useStableCallback,
    type FormOnSubmitNotAllowedFn,
    type PropertyPath
} from "@ngt-form/core";
import type { ReactNode } from "react";

import AppSettings from "src/AppSettings";
import type { AppIconDefinition } from "src/components/common/Icon";
import Icon from "src/components/common/Icon";
import type { TMDValidationError } from "src/types/TMDValidationError";

/**
 * Supplies a stable blocked-submit callback with optional notification presentation overrides.
 *
 * Postconditions: Creating the callback causes no notification or save. Falsy icon/message values
 * retain the default medium-error icon and message when the callback is invoked.
 */
const useTMDFormOnSubmitNotAllowed = (
    icon?: AppIconDefinition | null | undefined,
    message?: ReactNode | null | undefined
): FormOnSubmitNotAllowedFn<any, TMDValidationError, any> => {
    /**
     * Requests an error notification and returns only errors attributed to ngt-form-submit.
     *
     * Postconditions: On fulfillment, returns the existing error-array references or undefined when
     * that source has no errors. Other error sources and the supplied error argument are ignored.
     * The callback neither retries a save nor independently checks whether persistence occurred.
     */
    const onSubmitNotAllowed: FormOnSubmitNotAllowedFn<any, TMDValidationError, any> =
        useStableCallback(async (snapshot, _error, _formManager) => {
            notifications.show({
                color: "error",
                title: "Error",
                message: (
                    <Box>
                        {message ? (
                            message
                        ) : (
                            <>
                                The form was <strong> not </strong> saved.
                            </>
                        )}
                    </Box>
                ),
                icon: <Icon icon={icon ? icon : AppSettings.icons.error.medium} />
            });

            const formErrors = snapshot.formErrorsBySource["ngt-form-submit"] ?? [];
            const fieldErrors: Record<PropertyPath, TMDValidationError[]> = {};

            let hasErrors = formErrors?.length !== 0;

            for (let propertyPath of Object.keys(snapshot.fieldErrorsBySource)) {
                const propertyPathErrors =
                    snapshot.fieldErrorsBySource[propertyPath]["ngt-form-submit"];

                if (propertyPathErrors && propertyPathErrors.length !== 0) {
                    hasErrors = true;
                    fieldErrors[propertyPath] = propertyPathErrors;
                }
            }

            if (hasErrors) {
                return {
                    form: formErrors,
                    fields: fieldErrors
                };
            }

            return undefined;
        });

    return onSubmitNotAllowed;
};

export default useTMDFormOnSubmitNotAllowed;
